import React, { useState, useEffect, useCallback } from 'react';
import { useTour } from '@/contexts/TourContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  ChevronDown, 
  Plus, 
  Map, 
  Building2, 
  Share2,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  Edit3,
  Trash2,
  MoreVertical
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTourForm = {
  name: '',
  matterport_model_id: '',
  location: '',
  industry: 'industrial',
  client_id: '',
  client_name: '',
  project_name: '',
  job_number: ''
};

export function TourSelector({ className }) {
  const { tours, currentTour, selectTour, createTour, updateTour, deleteTour, loading } = useTour();
  const { canEdit, token } = useAuth();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tourToDelete, setTourToDelete] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [clients, setClients] = useState([]);
  
  const [newTour, setNewTour] = useState(emptyTourForm);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);
  const [editForm, setEditForm] = useState(emptyTourForm);

  const handleCreateTour = async () => {
    if (!newTour.name || !newTour.matterport_model_id) return;
    
    setCreating(true);
    const result = await createTour(newTour);
    setCreating(false);
    
    if (result.success) {
      setIsCreateDialogOpen(false);
      setNewTour(emptyTourForm);
    }
  };

  const openEditDialog = (tour, e) => {
    e.stopPropagation();
    setEditingTour(tour);
    setEditForm({
      name: tour.name || '',
      matterport_model_id: tour.matterport_model_id || '',
      location: tour.location || '',
      industry: tour.industry || 'industrial',
      client_id: tour.client_id || '',
      client_name: tour.client_name || '',
      project_name: tour.project_name || '',
      job_number: tour.job_number || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateTour = async () => {
    if (!editingTour || !editForm.name || !editForm.matterport_model_id) return;
    
    setSaving(true);
    const result = await updateTour(editingTour.id, editForm);
    setSaving(false);
    
    if (result.success) {
      setIsEditDialogOpen(false);
      setEditingTour(null);
    }
  };

  const openDeleteDialog = (tour, e) => {
    e.stopPropagation();
    setTourToDelete(tour);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteTour = async () => {
    if (!tourToDelete) return;
    
    const result = await deleteTour(tourToDelete.id);
    
    if (result.success) {
      setIsDeleteDialogOpen(false);
      setTourToDelete(null);
    }
  };

  const copyShareLink = () => {
    if (currentTour?.share_token) {
      const shareUrl = `${window.location.origin}/share/${currentTour.share_token}`;
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handler for new tour form changes
  const handleNewTourChange = (field, value) => {
    setNewTour(prev => ({ ...prev, [field]: value }));
  };

  // Handler for edit form changes
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Loading tours...</span>
      </div>
    );
  }

  return (
    <>
      <div className={cn("flex items-center gap-2", className)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 min-w-[200px] justify-between">
              <div className="flex items-center gap-2">
                <Map className="h-4 w-4 text-primary" />
                <span className="truncate">
                  {currentTour?.name || 'Select Tour'}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[360px]">
            <DropdownMenuLabel>Your Tours</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {tours.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No tours yet. Create your first tour!
              </div>
            ) : (
              tours.map((tour) => (
                <div
                  key={tour.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-accent rounded-sm",
                    tour.id === currentTour?.id && "bg-accent"
                  )}
                  onClick={() => selectTour(tour)}
                >
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-sm">{tour.name}</p>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      {tour.client_name && <span>{tour.client_name}</span>}
                      {tour.job_number && <span>• {tour.job_number}</span>}
                      {!tour.client_name && !tour.job_number && tour.location && (
                        <span>{tour.location}</span>
                      )}
                    </div>
                  </div>
                  {tour.id === currentTour?.id && (
                    <Badge variant="secondary" className="text-[10px] shrink-0">Active</Badge>
                  )}
                  {canEdit && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon-sm" className="h-6 w-6 shrink-0">
                          <MoreVertical className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => openEditDialog(tour, e)}>
                          <Edit3 className="h-3 w-3 mr-2" />
                          Edit Tour
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => openDeleteDialog(tour, e)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-2" />
                          Delete Tour
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))
            )}
            
            <DropdownMenuSeparator />
            {canEdit && (
              <DropdownMenuItem onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add New Tour
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Share Button */}
        {currentTour && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsShareDialogOpen(true)}
            title="Share this scan"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Create Tour Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Tour</DialogTitle>
            <DialogDescription>
              Connect a Matterport model to create a new tour.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select 
                value={newTour.client_id} 
                onValueChange={(value) => {
                  const client = clients.find(c => c.id === value);
                  handleNewTourChange('client_id', value);
                  if (client) {
                    handleNewTourChange('client_name', client.name);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3 w-3" />
                        {client.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Tour will be accessible to all users assigned to this client
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-job-number">Job Number</Label>
                <Input
                  id="new-job-number"
                  placeholder="e.g., ZE-2024-0042"
                  value={newTour.job_number}
                  onChange={(e) => handleNewTourChange('job_number', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-project-name">Project Name</Label>
                <Input
                  id="new-project-name"
                  placeholder="e.g., HS31 Survey"
                  value={newTour.project_name}
                  onChange={(e) => handleNewTourChange('project_name', e.target.value)}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-muted-foreground mb-3">Tour Details</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-tour-name">Tour Name *</Label>
              <Input
                id="new-tour-name"
                placeholder="e.g., Refinery Unit A"
                value={newTour.name}
                onChange={(e) => handleNewTourChange('name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-model-id">Matterport Model ID *</Label>
              <Input
                id="new-model-id"
                placeholder="e.g., 6szR5xa384b"
                value={newTour.matterport_model_id}
                onChange={(e) => handleNewTourChange('matterport_model_id', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Found in your Matterport URL: my.matterport.com/show/?m=<strong>MODEL_ID</strong>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-location">Location</Label>
              <Input
                id="new-location"
                placeholder="e.g., Aberdeen, UK"
                value={newTour.location}
                onChange={(e) => handleNewTourChange('location', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-industry">Industry</Label>
              <Select
                value={newTour.industry}
                onValueChange={(value) => handleNewTourChange('industry', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oil_gas">Oil & Gas</SelectItem>
                  <SelectItem value="facility">Facility</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                  <SelectItem value="marine">Marine</SelectItem>
                  <SelectItem value="surplus">Surplus Equipment</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="sato_canada">Sato Canada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTour} 
              disabled={!newTour.name || !newTour.matterport_model_id || creating}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Tour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tour Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Tour</DialogTitle>
            <DialogDescription>
              Update tour details and settings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select 
                value={editForm.client_id || ''} 
                onValueChange={(value) => {
                  const client = clients.find(c => c.id === value);
                  handleEditFormChange('client_id', value);
                  if (client) {
                    handleEditFormChange('client_name', client.name);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3 w-3" />
                        {client.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-job-number">Job Number</Label>
                <Input
                  id="edit-job-number"
                  placeholder="e.g., ZE-2024-0042"
                  value={editForm.job_number}
                  onChange={(e) => handleEditFormChange('job_number', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-project-name">Project Name</Label>
                <Input
                  id="edit-project-name"
                  placeholder="e.g., HS31 Survey"
                  value={editForm.project_name}
                  onChange={(e) => handleEditFormChange('project_name', e.target.value)}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-muted-foreground mb-3">Tour Details</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tour-name">Tour Name *</Label>
              <Input
                id="edit-tour-name"
                placeholder="e.g., Refinery Unit A"
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-model-id">Matterport Model ID *</Label>
              <Input
                id="edit-model-id"
                placeholder="e.g., 6szR5xa384b"
                value={editForm.matterport_model_id}
                onChange={(e) => handleEditFormChange('matterport_model_id', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Found in your Matterport URL: my.matterport.com/show/?m=<strong>MODEL_ID</strong>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-location">Location</Label>
              <Input
                id="edit-location"
                placeholder="e.g., Aberdeen, UK"
                value={editForm.location}
                onChange={(e) => handleEditFormChange('location', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-industry">Industry</Label>
              <Select
                value={editForm.industry}
                onValueChange={(value) => handleEditFormChange('industry', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oil_gas">Oil & Gas</SelectItem>
                  <SelectItem value="facility">Facility</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                  <SelectItem value="marine">Marine</SelectItem>
                  <SelectItem value="surplus">Surplus Equipment</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="sato_canada">Sato Canada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateTour} 
              disabled={!editForm.name || !editForm.matterport_model_id || saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tour?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{tourToDelete?.name}"? This action cannot be undone.
              All associated data (tickets, equipment, view positions) will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTour} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share This Scan</DialogTitle>
            <DialogDescription>
              Share "{currentTour?.name}" with others using the link below. They will only see this specific scan, not your entire portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/share/${currentTour?.share_token || ''}`}
                className="font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={copyShareLink}>
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Anyone with this link can view this scan without signing in</p>
              <p>• They will not have access to other tours or your portal</p>
              <p>• Editing features are disabled for shared viewers</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShareDialogOpen(false)}>
              Close
            </Button>
            <Button asChild>
              <a 
                href={`${window.location.origin}/share/${currentTour?.share_token || ''}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Preview Link
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TourSelector;
